<script lang="ts" setup>
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useDebounceFn } from "@vueuse/core";
import { type Assignments } from "classes/models/assignments/assignment.model";
import { AssignmentsService } from "classes/models/assignments/assignments.service";
import { Subscription } from "rxjs";
import { type Article } from "types/Article";
import { Checklists, type Checklist } from "types/Checklist";
import { useShowClassroomsLicenseReviewModal } from "~/composables/useShowClassroomsLicenseReviewModal";
import { GuidedTourEventName } from "~/types/enums/GuidedTourEventNames.enum";
import ArticleModal from "../Article/Modal.vue";
import AssignmentChecklistModal from "../Assignment/ChecklistModal.client.vue";

const user = useCurrentUser();

const selectedTab = ref<"ungraded" | "graded" | "summary">("ungraded");

const selectTab = (tab: "ungraded" | "graded" | "summary") => {
  selectedTab.value = tab;
};

const tabs = computed(() => {
  return [
    {
      key: "ungraded",
      label: `Ungraded (${ungradedDocuments.value.length})`,
      stepId: "documents-ungraded-tab",
    },
    {
      key: "graded",
      label: `Graded (${numGradedDocuments.value})`,
      stepId: "documents-graded-tab",
    },
    // {
    //   key: "summary",
    //   label: "Summary",
    //   stepId: "documents-summary-tab",
    // },
  ];
});

const levelFilter = ref<string | undefined>(undefined);
const lessonFilter = ref<string | undefined>(undefined);

const userDocumentsStore = useUserDocumentsStore();
const {
  homePageUngradedDocuments: ungradedDocuments,
  numGradedDocuments,
  hasOtherUserClassroomSubmissions,
  hasIniitalDocumentsDataLoaded,
} = storeToRefs(userDocumentsStore);
const gradedDocuments = ref<SubmittedDocuments>([]);

const filteredUngradedDocuments = computed(() => {
  const updatedDocs = ungradedDocuments.value.filter((document) => {
    const hasMatchingLevel = levelFilter.value
      ? document.levelId == levelFilter.value
      : true;

    const hasMatchingLesson = lessonFilter.value
      ? document.lessonId == lessonFilter.value
      : true;

    return hasMatchingLevel && hasMatchingLesson;
  });

  return [...updatedDocs];
});

const filteredGradedDocuments = computed(() => {
  const updateDocs = gradedDocuments.value.filter((document) => {
    const hasMatchingLevel = levelFilter.value
      ? document.levelId == levelFilter.value
      : true;

    const hasMatchingLesson = lessonFilter.value
      ? document.lessonId == lessonFilter.value
      : true;

    return hasMatchingLevel && hasMatchingLesson;
  });

  return [...updateDocs];
});

const isShowingModal = ref(false);

const activeAssignments = ref<Assignments>([]);

// Home page tour
const tourId = "iIqLAx35lYCvRVqe81cH";

// Advanced Tour
const advancedTourId = "0GLBSzr79wveL8Zxbeu2";

const userChecklistsSubscription = ref<Subscription | undefined>();
const assignmentSubscription = ref<Subscription | undefined>();

const horizontalLayout = ref<{ left: number; right: number }>({
  left: 0,
  right: 0,
});

const userChecklistStore = useUserChecklistsStore();
const { checklists } = storeToRefs(userChecklistStore);

onMounted(() => {
  initializeLayout();

  // userChecklistsSubscription.value = useUserChecklists()
  //   .streamUserChecklists()
  //   .subscribe((newChecklists: Checklists) => {
  //     checklists.value = newChecklists;
  //   });

  const currentUid = useCurrentUID();

  checkClassroomLicenses();

  if (currentUid) {
    assignmentSubscription.value =
      AssignmentsService.streamAllActiveAssignments(currentUid).subscribe(
        (assignments) => {
          activeAssignments.value = assignments;
        }
      );
  }

  // If the main tour has not be completed then we're going to show that instead of checking for a tip to show
  if (isTourComplete.value(tourId) != true) return;

  checkUserTips();
});

onUnmounted(() => {
  userChecklistsSubscription.value?.unsubscribe();
  assignmentSubscription.value?.unsubscribe();
});

const guidedTourStore = useGuidedTours();
const { allTours } = storeToRefs(guidedTourStore);

const appUserStore = useAppUserData();
const {
  isTourComplete,
  appUser,
  shouldHideSubmissionNotice,
  isInitialized,
  showOtherUsersSubmissions,
} = storeToRefs(appUserStore);

const tourEventSubscription = ref<Subscription | undefined>();

watch([allTours, gradedDocuments], () => {
  checkShowTutorials();

  if (
    isTourComplete.value(tourId) != true &&
    useGuidedTours().hasActiveTour != true
  ) {
    useGuidedTours().setTourToShow(tourId);
  }

  // So what we want to do here is if the user has not compelted the advanced tour and htey have completed more than 5 documents then show the next one
  // This tour is not yet ready yet.
  // TODO: Uncomment out this block of code one Heidi has approved the advanced tour.
  // if (
  //   isTourComplete.value(advancedTourId) != true &&
  //   gradedDocuments.value.length > 5 &&
  //   useGuidedTours().hasActiveTour != true
  // ) {
  //   useGuidedTours().setTourToShow(advancedTourId);
  // }

  if (tourEventSubscription.value) {
    tourEventSubscription.value.unsubscribe();
  }

  tourEventSubscription.value = useTourEventBus().events.subscribe(
    async (event) => {
      if (!event) return;

      if (
        event.name == GuidedTourEventName.tourComplete &&
        event.data != undefined
      ) {
        await appUserStore.markTourAsComplete(event.data as string);
      }
    }
  );
});

const userClassroomsData = useUserClassroomsDataStore();
const {
  hasAssignmentsWithoutChecklists,
  activeClassrooms,
  allAssignments,
  allStudents,
} = storeToRefs(userClassroomsData);

watch(hasAssignmentsWithoutChecklists, () => {
  // Ok let's open up a modal.
  if (
    hasAssignmentsWithoutChecklists.value == true &&
    isShowingModal.value != true
  ) {
    isShowingModal.value = true;

    const { open, close } = useDialog({
      title: "Missing Checklists",
      component: AssignmentChecklistModal,
      props: {
        onClose: () => {
          isShowingModal.value = false;
          close();
        },
      },
    });

    open();
  }
});

const activeArticle = ref<Article | undefined>(undefined);
const checkUserTips = async () => {
  if (isShowingModal.value == true) return;

  const article = await useUserTips().getUserTip();

  if (article == undefined) return;

  isShowingModal.value = true;
  activeArticle.value = article;

  console.log("article", article);

  const { open, close } = useDialog({
    title: article.title,
    component: ArticleModal,
    props: {
      article: article,
      onClose: () => {
        isShowingModal.value = false;
        close();
      },
    },
  });

  open();
};

const showFilters = ref(false);
const toggleShowFilters = () => {
  showFilters.value = !showFilters.value;
};

const route = useRoute();

const checkShowTutorials = () => {
  if (useGuidedTours().hasActiveTour == true) return;

  const queryParams = route.query;

  if ((queryParams.showTutorial as any) != "true") return;

  if ((queryParams.tutorialId as any) == undefined) return;

  const tutorialId = queryParams.tutorialId as string;

  const tour = allTours.value.find((tour) => tour.id == tutorialId);

  if (tour == undefined) return;

  useGuidedTours().startTour(tour);
  navigateTo("/");
};

// Maybe I'll move this to the middlware or to it's own middleware. That's not a bad idea.
const capabilityService = useCapabilitiesServiceStore();
const {
  hasEnoughClassroomLicenses,
  userHasOtherClassrooms,
  organizationHasEnoughClassrooms,
} = storeToRefs(capabilityService);

watch(hasEnoughClassroomLicenses, () => {
  checkClassroomLicenses();
});

const checkClassroomLicenses = () => {
  if (hasEnoughClassroomLicenses.value == true) return;

  useShowClassroomsLicenseReviewModal();
};

watch(
  () => [userHasOtherClassrooms.value, organizationHasEnoughClassrooms.value],
  () => {
    if (userHasOtherClassrooms.value != true) return;

    if (organizationHasEnoughClassrooms.value == true) return;

    console.log(hasEnoughClassroomLicenses.value);

    useBaseAlert(
      "Oops!",
      "Your organization does not have enough classroom licenses. Please contact your administrator to purchase more licenses."
    );
  }
);

const onUpdateShowOtherUsersSubmissions = async (value: boolean) => {
  if (appUser.value == undefined) return;

  appUser.value.showOtherUsersSubmissions = value;

  await appUserStore.saveUser();
};

const appUserDataStore = useAppUserData();
const {
  hasClassroomMode,
  hasLMSConfig,
  hasInitializedUserLMSConfig,
  showOtherUsersClassrooms,
  homePageVerticalLayout,
  homePageHorizontalLayout,
} = storeToRefs(appUserDataStore);

const showClassroomSideBar = computed(() => {
  if (hasInitializedUserLMSConfig.value == false) return false;

  return hasClassroomMode.value == true || hasLMSConfig.value == true;
});

const onHorizontalLayoutChanged = useDebounceFn((event: any) => {
  if (event[0] == 0 || event[0] == 100) {
    return;
  }

  appUserDataStore.updateHomePageHorizontalLayout(event[0]);
}, 300);

watch(showClassroomSideBar, () => {
  initializeLayout();
});

const initializeLayout = () => {
  let horizontalLayoutValue = 25;

  if (homePageHorizontalLayout.value != undefined) {
    if (horizontalLayoutValue < 10 || horizontalLayoutValue == 100) {
      horizontalLayoutValue = 25;
    }
  }

  horizontalLayout.value.left = horizontalLayoutValue;
  horizontalLayout.value.right = 100 - horizontalLayoutValue;
};
</script>

<template>
  <div
    v-if="
      hasInitializedUserLMSConfig &&
      user &&
      (horizontalLayout.left > 0 || horizontalLayout.right > 0)
    "
    class="pb-0 h-full flex flex-col overflow-hidden"
    data-step-id="documents-dashboard-container"
  >
    <ResizablePanelGroup
      class="flex flex-row items-start grow min-h-0 border-t"
      id="demo-group-1"
      direction="horizontal"
      @layout="onHorizontalLayoutChanged"
    >
      <ResizablePanel
        v-if="showClassroomSideBar"
        class="w-[200px] h-full"
        :default-size="horizontalLayout.left"
      >
        <DocumentsClassroomsSideBar class="bg-surface" />
      </ResizablePanel>
      <div
        v-if="showClassroomSideBar"
        class="relative h-full w-[6px] border-r border-l"
      >
        <ResizableHandle id="demo-handle-2" class="h-full bg-transparent" />
        <div class="absolute h-full top-0 w-full">
          <div class="flex flex-col items-center justify-center h-full w-full">
            <BaseIconGripVertical size="12" />
          </div>
        </div>
      </div>
      <ResizablePanel
        class="flex flex-col h-full grow min-h-0"
        :default-size="horizontalLayout.right"
      >
        <div class="flex flex-col w-full h-full min-h-0 bg-surface px-4 pt-4">
          <div
            class="w-full min-h-0 h-full flex flex-col"
            :class="{
              'mx-auto max-w-[1000px]': showClassroomSideBar != true,
            }"
          >
            <div class="flex flex-row items-center justify-end pb-4 w-full">
              <UploadStudentSubmissionButton
                v-if="checklists"
                data-step-id="student-upload-button"
                :classrooms="activeClassrooms"
                :assignments="allAssignments"
                :students="allStudents"
                :checklists="checklists"
                :show-samples="true"
                class="shrink-0"
                popup-alignment="start"
              >
                <template #trigger>
                  <BaseOutlinedButton color="secondary">
                    <div class="flex flex-row items-center">
                      <BaseIconUpload class="mr-2" />
                      Upload Student Submissions
                    </div>
                  </BaseOutlinedButton>
                </template>
              </UploadStudentSubmissionButton>
              <div class="flex flex-row items-center justify-between grow">
                <BaseLabelBadge
                  v-if="isInitialized && shouldHideSubmissionNotice == false"
                  color="warning"
                  class="ml-4 min-h-[60px]"
                  align-items="center"
                >
                  <template #icon>
                    <BaseIconArrowLeft size="20" class="mr-2 mt-[1px]" />
                  </template>
                  <div class="flex flex-row items-center">
                    <span>
                      Click to access sample papers or upload your student
                      submissions.
                    </span>
                    <BaseTextButton
                      class="ml-2"
                      @click="appUserStore.hideSubmissionNotice()"
                    >
                      Hide
                    </BaseTextButton>
                  </div>
                </BaseLabelBadge>
              </div>
            </div>

            <BaseTabBar
              :tabs="tabs"
              v-model="selectedTab"
              class="shrink-0 mb-2"
            />

            <div
              class="grow min-h-0 overflow-y-auto overflow-x-auto w-full flex flex-col"
              data-step-id="document-lists-container"
              v-auto-animate
            >
              <div
                v-if="hasIniitalDocumentsDataLoaded != true"
                class="flex flex-col h-full w-full items-center justify-start max-w-list-item-md"
              >
                <div class="flex flex-row items-center gap-2 w-full mb-4">
                  <BaseSkeleton class="h-[40px] grow" />
                  <BaseSkeleton class="h-[30px] w-[30px]" />
                  <BaseSkeleton class="h-[30px] w-[30px]" />
                </div>
                <div class="flex flex-col gap-2 w-full">
                  <BaseCard
                    v-for="i in 15"
                    :key="i"
                    class="w-full p-4 flex flex-row items-center justify-between"
                  >
                    <div class="flex flex-col gap-2 grow">
                      <BaseSkeleton class="h-[20px] w-[400px]" />
                      <BaseSkeleton class="h-[18px] w-[300px]" />
                    </div>
                    <div class="flex flex-row items-center gap-2">
                      <BaseSkeleton class="h-[40px] w-[180px]" />
                      <BaseSkeleton class="h-[30px] w-[30px]" />
                      <BaseSkeleton class="h-[30px] w-[30px]" />
                    </div>
                  </BaseCard>
                </div>
              </div>
              <div
                v-if="
                  selectedTab == 'ungraded' && hasIniitalDocumentsDataLoaded
                "
                class="overflow-y-auto w-full"
              >
                <DocumentsList
                  :key="filteredUngradedDocuments.length"
                  :documents="filteredUngradedDocuments"
                  align="left"
                  :allow-filters="true"
                />
              </div>

              <DocumentsInfiniteList
                v-if="selectedTab == 'graded' && hasIniitalDocumentsDataLoaded"
                :level-id="levelFilter"
                :lesson-id="lessonFilter"
                :states="[DocumentState.graded]"
                align="right"
                class="overflow-y-auto"
              />
            </div>
          </div>
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
  </div>
</template>

<style scoped></style>
